import React, { useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { MessagesSquare, Megaphone, ShieldCheck, History } from "lucide-react";
import Button from "../common/Button";
import Modal from "../common/Modal";
import Switch from "../common/Switch";
import { updateEvent } from "../../services/createEventService";
import { setChatRoomMediaApproval } from "../../services/chatMediaApprovalService";
import { CHAT_HISTORY_VISIBILITY_MODE, setChatHistoryVisibility } from "../../services/chatHistoryVisibilityService";
import { color, radius } from "../../styles/theme";

/**
 * Chat communication mode — Event.IsChatEnabled, read from the same
 * PUT /Events/{id} endpoint every other single-field event edit in this
 * panel already uses (see RoomHomeHeader.jsx's description save). Confirmed
 * against EventCrudService.cs: true = "Two-Way" (everyone can send),
 * false = "One-Way" (only the event admin can send) — the push-notification
 * strings it sends on change are literally TwoWayChatEnabledMessage /
 * OneWayChatEnabledMessage. Not currently enforced by the chat hub itself
 * (ChatHub.SendMessage has no check against this flag for any client) — this
 * panel enforces it locally by disabling its own composer for non-admins,
 * same as mobile is expected to.
 *
 * IMPORTANT: PutEventAsync assigns a couple of fields (IsWatermark,
 * IsJoinRequestDisabled) straight from the request with no null-coalescing
 * fallback to the existing value — sending a minimal { isChatEnabled }
 * body would silently reset those to false. So this sends the full,
 * already-loaded event object back with only IsChatEnabled overridden,
 * not a partial payload.
 */
const MODE_OPTIONS = [
  { value: true, key: "twoWay", icon: MessagesSquare, tone: "success" },
  { value: false, key: "oneWay", icon: Megaphone, tone: "warning" },
];

const TONE = {
  success: { bg: color.success50, fg: color.success500 },
  warning: { bg: color.warning50, fg: color.warning500 },
};

const ChatSettingsModal = ({ eventId, event, mediaApprovalEligible, isARoomEvent, onClose, onUpdated }) => {
  const { t } = useTranslation("chat");
  const [saving, setSaving] = useState(false);
  const currentMode = event?.IsChatEnabled !== false;
  // ChatRoom.RequireMediaApproval — comes through as part of the already-
  // loaded Event.ChatRoom (GetEventAsync .Includes it), same source
  // IsChatEnabled above reads from.
  const [requireMediaApproval, setRequireMediaApproval] = useState(!!event?.ChatRoom?.RequireMediaApproval);
  const [savingApproval, setSavingApproval] = useState(false);

  // ChatRoom.ChatHistoryVisibilityMode/Value — same source as
  // RequireMediaApproval above. Only meaningful for @Room chats; ordinary
  // Event chats follow the fixed Open-Door cutoff instead (see isARoomEvent).
  const [historyMode, setHistoryMode] = useState(
    event?.ChatRoom?.ChatHistoryVisibilityMode ?? CHAT_HISTORY_VISIBILITY_MODE.MessageCount
  );
  const [historyValue, setHistoryValue] = useState(event?.ChatRoom?.ChatHistoryVisibilityValue ?? 50);
  const [savingHistoryVisibility, setSavingHistoryVisibility] = useState(false);
  const historyDirty =
    historyMode !== (event?.ChatRoom?.ChatHistoryVisibilityMode ?? CHAT_HISTORY_VISIBILITY_MODE.MessageCount) ||
    (historyMode !== CHAT_HISTORY_VISIBILITY_MODE.FullHistory &&
      Number(historyValue) !== (event?.ChatRoom?.ChatHistoryVisibilityValue ?? 50));

  const handleSelect = async (isChatEnabled) => {
    if (isChatEnabled === currentMode || saving) return;
    setSaving(true);
    try {
      await updateEvent(eventId, { ...event, IsChatEnabled: isChatEnabled });
      toast.success(isChatEnabled ? t("settings.toasts.twoWayEnabled") : t("settings.toasts.oneWayEnabled"));
      onUpdated();
    } catch (err) {
      toast.error(err.response?.data?.Message || t("settings.toasts.updateError"));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleMediaApproval = async () => {
    if (savingApproval) return;
    const next = !requireMediaApproval;
    setSavingApproval(true);
    try {
      await setChatRoomMediaApproval(eventId, next);
      setRequireMediaApproval(next);
      toast.success(next ? t("settings.mediaApproval.toasts.enabled") : t("settings.mediaApproval.toasts.disabled"));
    } catch (err) {
      toast.error(err.response?.data?.Message || t("settings.mediaApproval.toasts.updateError"));
    } finally {
      setSavingApproval(false);
    }
  };

  const handleSaveHistoryVisibility = async () => {
    if (savingHistoryVisibility || !historyDirty) return;
    if (historyMode !== CHAT_HISTORY_VISIBILITY_MODE.FullHistory && Number(historyValue) <= 0) {
      toast.error(t("settings.historyVisibility.toasts.invalidValue"));
      return;
    }
    setSavingHistoryVisibility(true);
    try {
      await setChatHistoryVisibility(eventId, historyMode, Number(historyValue));
      toast.success(t("settings.historyVisibility.toasts.updated"));
      onUpdated();
    } catch (err) {
      toast.error(err.response?.data?.Message || t("settings.historyVisibility.toasts.updateError"));
    } finally {
      setSavingHistoryVisibility(false);
    }
  };

  return (
    <Modal
      title={t("settings.title")}
      subtitle={t("settings.subtitle")}
      onClose={onClose}
      busy={saving || savingApproval || savingHistoryVisibility}
      maxWidth={420}
      footer={<Button variant="secondary" onClick={onClose} disabled={saving || savingApproval || savingHistoryVisibility}>{t("settings.close")}</Button>}
    >
      <div style={s.list}>
        {MODE_OPTIONS.map((opt) => {
          const tone = TONE[opt.tone];
          const active = opt.value === currentMode;
          return (
            <button
              key={String(opt.value)}
              type="button"
              className="row-link"
              style={{ ...s.item, ...(active ? s.itemActive : {}) }}
              onClick={() => handleSelect(opt.value)}
              disabled={saving || savingApproval}
            >
              <span style={{ ...s.itemIcon, background: tone.bg, color: tone.fg }}>
                <opt.icon size={16} strokeWidth={2} />
              </span>
              <span style={{ flex: 1, textAlign: "left" }}>
                <span style={s.itemLabel}>{t(`settings.options.${opt.key}.label`)}</span>
                <span style={s.itemDesc}>{t(`settings.options.${opt.key}.description`)}</span>
              </span>
              {active && <span style={s.currentTag}>{t("settings.currentTag")}</span>}
            </button>
          );
        })}
      </div>

      {mediaApprovalEligible && (
        <>
          <div style={s.divider} />

          <div style={s.item}>
            <span style={{ ...s.itemIcon, background: color.warning50, color: color.warning500 }}>
              <ShieldCheck size={16} strokeWidth={2} />
            </span>
            <span style={{ flex: 1, textAlign: "left" }}>
              <span style={s.itemLabel}>{t("settings.mediaApproval.label")}</span>
              <span style={s.itemDesc}>{t("settings.mediaApproval.description")}</span>
            </span>
            <Switch checked={requireMediaApproval} onChange={handleToggleMediaApproval} disabled={savingApproval} />
          </div>
        </>
      )}

      {isARoomEvent && (
        <>
          <div style={s.divider} />

          <div style={{ ...s.item, alignItems: "flex-start", flexDirection: "column", gap: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, width: "100%" }}>
              <span style={{ ...s.itemIcon, background: color.primary50, color: color.primary600 }}>
                <History size={16} strokeWidth={2} />
              </span>
              <span style={{ flex: 1, textAlign: "left" }}>
                <span style={s.itemLabel}>{t("settings.historyVisibility.label")}</span>
                <span style={s.itemDesc}>{t("settings.historyVisibility.description")}</span>
              </span>
            </div>

            <div style={{ display: "flex", gap: 8, width: "100%" }}>
              <select
                className="input"
                style={{ flex: historyMode === CHAT_HISTORY_VISIBILITY_MODE.FullHistory ? 1 : 1.4 }}
                value={historyMode}
                disabled={savingHistoryVisibility}
                onChange={(e) => setHistoryMode(Number(e.target.value))}
              >
                <option value={CHAT_HISTORY_VISIBILITY_MODE.MessageCount}>{t("settings.historyVisibility.modes.messageCount")}</option>
                <option value={CHAT_HISTORY_VISIBILITY_MODE.TimePeriod}>{t("settings.historyVisibility.modes.timePeriod")}</option>
                <option value={CHAT_HISTORY_VISIBILITY_MODE.FullHistory}>{t("settings.historyVisibility.modes.fullHistory")}</option>
              </select>

              {historyMode !== CHAT_HISTORY_VISIBILITY_MODE.FullHistory && (
                <input
                  type="number"
                  min={1}
                  className="input"
                  style={{ flex: 1 }}
                  value={historyValue}
                  disabled={savingHistoryVisibility}
                  onChange={(e) => setHistoryValue(e.target.value)}
                  placeholder={
                    historyMode === CHAT_HISTORY_VISIBILITY_MODE.MessageCount
                      ? t("settings.historyVisibility.messageCountPlaceholder")
                      : t("settings.historyVisibility.timePeriodPlaceholder")
                  }
                />
              )}
            </div>

            <Button
              size="sm"
              onClick={handleSaveHistoryVisibility}
              disabled={savingHistoryVisibility || !historyDirty}
              style={{ alignSelf: "flex-end" }}
            >
              {t("settings.historyVisibility.save")}
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
};

const s = {
  list: { display: "flex", flexDirection: "column", gap: 8 },
  divider: { height: 1, background: color.border, margin: "16px 0" },
  item: {
    display: "flex", alignItems: "center", gap: 12,
    padding: "12px 14px", borderRadius: radius.sm, border: `1px solid ${color.border}`, background: color.surface,
  },
  itemActive: { borderColor: color.primary500, background: color.primary50 },
  itemIcon: {
    width: 32, height: 32, borderRadius: radius.sm, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
  },
  itemLabel: { display: "block", fontSize: 13.5, fontWeight: 700, color: color.textPrimary },
  itemDesc: { display: "block", fontSize: 11.5, color: color.textMuted, marginTop: 1 },
  currentTag: { fontSize: 10.5, fontWeight: 700, color: color.primary600, textTransform: "uppercase", letterSpacing: 0.4, flexShrink: 0 },
};

export default ChatSettingsModal;
